import * as Y from 'yjs';
import type { DocumentAdapter } from '../domain/adapter.js';
import { NotFoundError } from '../domain/errors.js';
import { isCalendarDay, normalizeCookDateScale } from '../domain/validation.js';
import type {
  CollectionRecord,
  CollectionScalarField,
  CookDateRecord,
  DayNoteRecord,
  IngredientField,
  IngredientRecord,
  InstructionRecord,
  NutritionFactField,
  NutritionFactRecord,
  PhotoField,
  PhotoRecord,
  RecipeMemberships,
  RecipeRecord,
  RecipeScalarField,
  RecipeTextField,
} from '../domain/records.js';
import type {
  CollectionId,
  CookDateId,
  DayNoteId,
  ImportProvenance,
  IngredientId,
  InstructionId,
  NutritionBasis,
  NutritionFactId,
  PhotoId,
  RecipeId,
  SourceType,
  Unsubscribe,
  UserId,
} from '../domain/types.js';
import { applyTextDiff } from './text.js';

export const ROOT_META = 'meta';
export const ROOT_COLLECTIONS = 'collections';
export const ROOT_RECIPES = 'recipes';
export const ROOT_FAVOURITES = 'favourites';
export const ROOT_DAY_NOTES = 'dayNotes';

type YRecipe = Y.Map<unknown>;
type YCollection = Y.Map<unknown>;

/**
 * The Yjs-backed adapter, and the only place in the repository that imports `yjs`.
 *
 * The merge behaviour of the whole application is decided here, by which Yjs type backs
 * each field:
 *
 *   - Short scalars are plain values in a Y.Map, which is a last-write-wins register. Two
 *     people renaming a recipe produce one clean title rather than interleaved characters.
 *   - Long prose is Y.Text, which merges character by character. That is what you want for
 *     a step someone is rewriting while you fix a typo in it.
 *   - Reorderable lists are keyed Y.Maps carrying a fractional index, never Y.Arrays.
 *     Yjs has no move operation, so a Y.Array move is delete-then-insert and concurrent
 *     moves of one row duplicate it.
 *   - Sets (collections, tags) are Y.Maps keyed by member, so concurrent add and remove of
 *     different members never collide.
 *   - Day notes are one root Y.Map keyed by note id, like cook dates, so two people adding
 *     a note to the same day at once keep both. A note's text is a plain value: a short
 *     label should never interleave.
 */
export class YjsDocumentAdapter implements DocumentAdapter {
  private readonly listeners = new Set<(origin: string | null) => void>();
  private readonly onUpdate: (update: Uint8Array, origin: unknown) => void;

  constructor(readonly doc: Y.Doc) {
    this.onUpdate = (_update, origin) => {
      const label = typeof origin === 'string' ? origin : null;
      for (const listener of [...this.listeners]) listener(label);
    };
    this.doc.on('update', this.onUpdate);
  }

  private get metaMap(): Y.Map<unknown> {
    return this.doc.getMap(ROOT_META);
  }

  private get collectionsMap(): Y.Map<YCollection> {
    return this.doc.getMap(ROOT_COLLECTIONS);
  }

  private get recipesMap(): Y.Map<YRecipe> {
    return this.doc.getMap(ROOT_RECIPES);
  }

  private get favouritesMap(): Y.Map<Y.Map<number>> {
    return this.doc.getMap(ROOT_FAVOURITES);
  }

  private get dayNotesMap(): Y.Map<Y.Map<unknown>> {
    return this.doc.getMap(ROOT_DAY_NOTES);
  }

  transact<T>(fn: () => T, origin: string): T {
    return this.doc.transact(fn, origin);
  }

  getMeta<T = unknown>(key: string): T | undefined {
    const value = this.metaMap.get(key);
    return value === undefined ? undefined : (value as T);
  }

  setMeta(key: string, value: unknown): void {
    this.metaMap.set(key, value);
  }

  // ------------------------------------------------------------ collections

  listCollections(): CollectionRecord[] {
    const out: CollectionRecord[] = [];
    for (const [id, node] of this.collectionsMap.entries()) out.push(readCollection(id, node));
    return out;
  }

  getCollection(id: CollectionId): CollectionRecord | null {
    const node = this.collectionsMap.get(id);
    return node == null ? null : readCollection(id, node);
  }

  createCollection(record: CollectionRecord): void {
    if (this.collectionsMap.has(record.id)) return;
    const node = new Y.Map<unknown>();
    this.collectionsMap.set(record.id, node);
    node.set('name', record.name);
    node.set('description', record.description);
    node.set('icon', record.icon);
    node.set('order', record.order);
    node.set('isSystem', record.isSystem);
    node.set('createdAt', record.createdAt);
    node.set('updatedAt', record.updatedAt);
    node.set('deletedAt', record.deletedAt);
  }

  setCollectionField(id: CollectionId, field: CollectionScalarField, value: unknown): void {
    const node = this.collectionsMap.get(id);
    if (node == null) throw new NotFoundError('Collection', id);
    node.set(field, value);
  }

  // ------------------------------------------------------------ recipes

  listRecipes(): RecipeRecord[] {
    const out: RecipeRecord[] = [];
    for (const [id, node] of this.recipesMap.entries()) out.push(readRecipe(id, node));
    return out;
  }

  getRecipe(id: RecipeId): RecipeRecord | null {
    const node = this.recipesMap.get(id);
    return node == null ? null : readRecipe(id, node);
  }

  countLiveRecipes(): number {
    let count = 0;
    for (const node of this.recipesMap.values()) {
      if (node.get('deletedAt') == null) count += 1;
    }
    return count;
  }

  listRecipeMemberships(): RecipeMemberships[] {
    const out: RecipeMemberships[] = [];
    for (const [id, node] of this.recipesMap.entries()) {
      out.push({ id, collections: readCollections(node) });
    }
    return out;
  }

  listCollectionMembers(collectionId: CollectionId): { id: RecipeId; order: string }[] {
    const members: { id: RecipeId; order: string }[] = [];
    for (const [recipeId, node] of this.recipesMap.entries()) {
      const collections = node.get('collections');
      if (!(collections instanceof Y.Map)) continue;
      const order = collections.get(collectionId);
      if (typeof order === 'string') members.push({ id: recipeId, order });
    }
    return members;
  }

  createRecipe(record: RecipeRecord): void {
    if (this.recipesMap.has(record.id)) return;
    const node = new Y.Map<unknown>();
    this.recipesMap.set(record.id, node);

    node.set('id', record.id);
    node.set('title', record.title);
    node.set('sourceType', record.sourceType);
    node.set('sourceUrl', record.sourceUrl);
    node.set('sourceText', record.sourceText);
    node.set('servings', record.servings);
    node.set('prepMinutes', record.prepMinutes);
    node.set('cookMinutes', record.cookMinutes);
    node.set('nutritionBasis', record.nutritionBasis);
    node.set('createdAt', record.createdAt);
    node.set('createdBy', record.createdBy);
    node.set('updatedAt', record.updatedAt);
    node.set('updatedBy', record.updatedBy);
    node.set('deletedAt', record.deletedAt);
    node.set('importedFrom', record.importedFrom);

    const description = new Y.Text();
    node.set('description', description);
    if (record.description) description.insert(0, record.description);

    const notes = new Y.Text();
    node.set('notes', notes);
    if (record.notes) notes.insert(0, record.notes);

    node.set('ingredients', new Y.Map<unknown>());
    node.set('instructions', new Y.Map<unknown>());
    node.set('photos', new Y.Map<unknown>());
    node.set('cookDates', new Y.Map<unknown>());
    node.set('nutritionFacts', new Y.Map<unknown>());

    const collections = new Y.Map<string>();
    node.set('collections', collections);
    for (const [collectionId, order] of Object.entries(record.collections)) {
      collections.set(collectionId, order);
    }

    const tags = new Y.Map<boolean>();
    node.set('tags', tags);
    for (const tag of record.tags) tags.set(tag, true);
  }

  setRecipeField(id: RecipeId, field: RecipeScalarField, value: unknown): void {
    this.requireRecipe(id).set(field, value);
  }

  setRecipeText(id: RecipeId, field: RecipeTextField, value: string): void {
    const node = this.requireRecipe(id);
    let text = node.get(field);
    if (!(text instanceof Y.Text)) {
      text = new Y.Text();
      node.set(field, text);
    }
    applyTextDiff(text as Y.Text, value);
  }

  setRecipeCollection(id: RecipeId, collectionId: CollectionId, order: string | null): void {
    const map = this.requireChildMap(id, 'collections') as Y.Map<string>;
    if (order == null) map.delete(collectionId);
    else map.set(collectionId, order);
  }

  setRecipeTag(id: RecipeId, tag: string, present: boolean): void {
    const map = this.requireChildMap(id, 'tags') as Y.Map<boolean>;
    if (present) map.set(tag, true);
    else map.delete(tag);
  }

  isFavourite(userId: UserId, recipeId: RecipeId): boolean {
    return this.getFavouritedAt(userId, recipeId) != null;
  }

  getFavouritedAt(userId: UserId, recipeId: RecipeId): number | null {
    const userMap = this.favouritesMap.get(userId);
    if (!(userMap instanceof Y.Map)) return null;
    const value = userMap.get(recipeId);
    if (typeof value === 'number') return value;
    // Older clients wrote `true` instead of a timestamp.
    if (value === true) return 0;
    return null;
  }

  setFavouritedAt(userId: UserId, recipeId: RecipeId, favouritedAt: number | null): void {
    const map = this.requireUserFavourites(userId);
    if (favouritedAt == null) map.delete(recipeId);
    else map.set(recipeId, favouritedAt);
  }

  listFavouriteIds(userId: UserId): RecipeId[] {
    const userMap = this.favouritesMap.get(userId);
    if (!(userMap instanceof Y.Map)) return [];
    const out: RecipeId[] = [];
    for (const [recipeId, value] of userMap.entries()) {
      if (typeof value === 'number' || value === true) out.push(recipeId);
    }
    return out;
  }

  // ------------------------------------------------------------ children

  addIngredient(recipeId: RecipeId, record: IngredientRecord): void {
    const map = this.requireChildMap(recipeId, 'ingredients');
    if (map.has(record.id)) return;
    const node = new Y.Map<unknown>();
    map.set(record.id, node);
    node.set('order', record.order);
    node.set('groupHeading', record.groupHeading);
    node.set('quantity', record.quantity);
    node.set('unit', record.unit);
    node.set('name', record.name);
    node.set('note', record.note);
  }

  setIngredientField(
    recipeId: RecipeId,
    ingredientId: IngredientId,
    field: IngredientField,
    value: unknown,
  ): void {
    const node = this.requireChildMap(recipeId, 'ingredients').get(ingredientId);
    if (!(node instanceof Y.Map)) return;
    node.set(field, value);
  }

  /**
   * Children are hard-deleted, unlike recipes. Deleting an ingredient *should* beat a
   * concurrent correction of its quantity — the row is gone either way — whereas deleting
   * a whole recipe out from under someone's edit would be real data loss, which is why
   * recipes carry a `deletedAt` field instead.
   */
  removeIngredient(recipeId: RecipeId, ingredientId: IngredientId): void {
    this.requireChildMap(recipeId, 'ingredients').delete(ingredientId);
  }

  addInstruction(recipeId: RecipeId, record: InstructionRecord): void {
    const map = this.requireChildMap(recipeId, 'instructions');
    if (map.has(record.id)) return;
    const node = new Y.Map<unknown>();
    map.set(record.id, node);
    node.set('order', record.order);
    const text = new Y.Text();
    node.set('text', text);
    if (record.text) text.insert(0, record.text);
  }

  setInstructionOrder(recipeId: RecipeId, instructionId: InstructionId, order: string): void {
    const node = this.requireChildMap(recipeId, 'instructions').get(instructionId);
    if (!(node instanceof Y.Map)) return;
    node.set('order', order);
  }

  setInstructionText(recipeId: RecipeId, instructionId: InstructionId, value: string): void {
    const node = this.requireChildMap(recipeId, 'instructions').get(instructionId);
    if (!(node instanceof Y.Map)) return;
    let text = node.get('text');
    if (!(text instanceof Y.Text)) {
      text = new Y.Text();
      node.set('text', text);
    }
    applyTextDiff(text as Y.Text, value);
  }

  removeInstruction(recipeId: RecipeId, instructionId: InstructionId): void {
    this.requireChildMap(recipeId, 'instructions').delete(instructionId);
  }

  addPhoto(recipeId: RecipeId, record: PhotoRecord): void {
    const map = this.requireChildMap(recipeId, 'photos');
    if (map.has(record.id)) return;
    const node = new Y.Map<unknown>();
    map.set(record.id, node);
    node.set('order', record.order);
    node.set('blobId', record.blobId);
    node.set('width', record.width);
    node.set('height', record.height);
    node.set('blurhash', record.blurhash);
  }

  setPhotoField(recipeId: RecipeId, photoId: PhotoId, field: PhotoField, value: unknown): void {
    const node = this.requireChildMap(recipeId, 'photos').get(photoId);
    if (!(node instanceof Y.Map)) return;
    node.set(field, value);
  }

  removePhoto(recipeId: RecipeId, photoId: PhotoId): void {
    this.requireChildMap(recipeId, 'photos').delete(photoId);
  }

  addCookDate(recipeId: RecipeId, record: CookDateRecord): void {
    const map = this.requireChildMap(recipeId, 'cookDates');
    if (map.has(record.id)) return;
    const node = new Y.Map<unknown>();
    map.set(record.id, node);
    node.set('date', record.date);
    if (record.scale != null) node.set('scale', record.scale);
  }

  removeCookDate(recipeId: RecipeId, cookDateId: CookDateId): void {
    this.requireChildMap(recipeId, 'cookDates').delete(cookDateId);
  }

  // ------------------------------------------------------------ day notes

  listDayNotes(): DayNoteRecord[] {
    const out: DayNoteRecord[] = [];
    // Any client can write this map, so anything that is not a real day with text is skipped.
    for (const [id, node] of this.dayNotesMap.entries()) {
      if (!(node instanceof Y.Map)) continue;
      const date = node.get('date');
      const text = node.get('text');
      const createdAt = node.get('createdAt');
      if (typeof date !== 'string' || !isCalendarDay(date)) continue;
      if (typeof text !== 'string' || text === '') continue;
      out.push({ id, date, text, createdAt: typeof createdAt === 'number' ? createdAt : 0 });
    }
    return out;
  }

  addDayNote(record: DayNoteRecord): void {
    const map = this.dayNotesMap;
    if (map.has(record.id)) return;
    const node = new Y.Map<unknown>();
    map.set(record.id, node);
    node.set('date', record.date);
    node.set('text', record.text);
    node.set('createdAt', record.createdAt);
  }

  removeDayNote(dayNoteId: DayNoteId): void {
    this.dayNotesMap.delete(dayNoteId);
  }

  addNutritionFact(recipeId: RecipeId, record: NutritionFactRecord): void {
    const map = this.requireChildMap(recipeId, 'nutritionFacts');
    if (map.has(record.id)) return;
    const node = new Y.Map<unknown>();
    map.set(record.id, node);
    node.set('order', record.order);
    node.set('label', record.label);
    node.set('amount', record.amount);
    node.set('unit', record.unit);
  }

  setNutritionFactField(
    recipeId: RecipeId,
    factId: NutritionFactId,
    field: NutritionFactField,
    value: unknown,
  ): void {
    const node = this.requireChildMap(recipeId, 'nutritionFacts').get(factId);
    if (!(node instanceof Y.Map)) return;
    node.set(field, value);
  }

  removeNutritionFact(recipeId: RecipeId, factId: NutritionFactId): void {
    this.requireChildMap(recipeId, 'nutritionFacts').delete(factId);
  }

  onChange(listener: (origin: string | null) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  destroy(): void {
    this.doc.off('update', this.onUpdate);
    this.listeners.clear();
  }

  private requireRecipe(id: RecipeId): YRecipe {
    const node = this.recipesMap.get(id);
    if (node == null) throw new NotFoundError('Recipe', id);
    return node;
  }

  private requireChildMap(recipeId: RecipeId, key: string): Y.Map<unknown> {
    const recipe = this.requireRecipe(recipeId);
    let map = recipe.get(key);
    if (!(map instanceof Y.Map)) {
      // A document written by an older client may predate this field. Materialise it
      // rather than failing, so an old and a new client can share a household.
      map = new Y.Map<unknown>();
      recipe.set(key, map);
    }
    return map as Y.Map<unknown>;
  }

  private requireUserFavourites(userId: UserId): Y.Map<number> {
    let map = this.favouritesMap.get(userId);
    if (!(map instanceof Y.Map)) {
      map = new Y.Map<number>();
      this.favouritesMap.set(userId, map);
    }
    return map;
  }
}

// -------------------------------------------------------------------- readers

function str(node: Y.Map<unknown>, key: string, fallback = ''): string {
  const value = node.get(key);
  if (typeof value === 'string') return value;
  if (value instanceof Y.Text) return value.toString();
  return fallback;
}

function num(node: Y.Map<unknown>, key: string, fallback: number): number {
  const value = node.get(key);
  return typeof value === 'number' ? value : fallback;
}

function nullableNum(node: Y.Map<unknown>, key: string): number | null {
  const value = node.get(key);
  return typeof value === 'number' ? value : null;
}

function readCollection(id: CollectionId, node: YCollection): CollectionRecord {
  return {
    id,
    name: str(node, 'name'),
    description: str(node, 'description'),
    icon: str(node, 'icon'),
    order: str(node, 'order', 'a0'),
    isSystem: node.get('isSystem') === true,
    createdAt: num(node, 'createdAt', 0),
    updatedAt: num(node, 'updatedAt', 0),
    deletedAt: nullableNum(node, 'deletedAt'),
  };
}

function readCollections(node: YRecipe): Record<CollectionId, string> {
  const collections: Record<CollectionId, string> = {};
  const collectionMap = node.get('collections');
  if (collectionMap instanceof Y.Map) {
    for (const [collectionId, order] of collectionMap.entries()) {
      if (typeof order === 'string') collections[collectionId] = order;
    }
  }
  return collections;
}

function readRecipe(id: RecipeId, node: YRecipe): RecipeRecord {
  const ingredients: IngredientRecord[] = [];
  const ingredientMap = node.get('ingredients');
  if (ingredientMap instanceof Y.Map) {
    for (const [itemId, item] of ingredientMap.entries()) {
      if (!(item instanceof Y.Map)) continue;
      ingredients.push({
        id: itemId,
        order: str(item, 'order', 'a0'),
        groupHeading: str(item, 'groupHeading'),
        quantity: str(item, 'quantity'),
        unit: str(item, 'unit'),
        name: str(item, 'name'),
        note: str(item, 'note'),
      });
    }
  }

  const instructions: InstructionRecord[] = [];
  const instructionMap = node.get('instructions');
  if (instructionMap instanceof Y.Map) {
    for (const [itemId, item] of instructionMap.entries()) {
      if (!(item instanceof Y.Map)) continue;
      instructions.push({ id: itemId, order: str(item, 'order', 'a0'), text: str(item, 'text') });
    }
  }

  const photos: PhotoRecord[] = [];
  const photoMap = node.get('photos');
  if (photoMap instanceof Y.Map) {
    for (const [itemId, item] of photoMap.entries()) {
      if (!(item instanceof Y.Map)) continue;
      photos.push({
        id: itemId,
        order: str(item, 'order', 'a0'),
        blobId: str(item, 'blobId'),
        width: num(item, 'width', 0),
        height: num(item, 'height', 0),
        blurhash: str(item, 'blurhash'),
      });
    }
  }

  const cookDates: CookDateRecord[] = [];
  const cookDateMap = node.get('cookDates');
  if (cookDateMap instanceof Y.Map) {
    for (const [itemId, item] of cookDateMap.entries()) {
      if (!(item instanceof Y.Map)) continue;
      const date = str(item, 'date');
      if (date === '') continue;
      const scale = normalizeCookDateScale(item.get('scale'));
      cookDates.push({ id: itemId, date, ...(scale != null && { scale }) });
    }
  }

  const nutritionFacts: NutritionFactRecord[] = [];
  const nutritionMap = node.get('nutritionFacts');
  if (nutritionMap instanceof Y.Map) {
    for (const [itemId, item] of nutritionMap.entries()) {
      if (!(item instanceof Y.Map)) continue;
      nutritionFacts.push({
        id: itemId,
        order: str(item, 'order', 'a0'),
        label: str(item, 'label'),
        amount: str(item, 'amount'),
        unit: str(item, 'unit'),
      });
    }
  }

  const collections = readCollections(node);

  const tags: string[] = [];
  const tagMap = node.get('tags');
  if (tagMap instanceof Y.Map) {
    for (const [tag, present] of tagMap.entries()) if (present) tags.push(tag);
  }

  const importedFromRaw = node.get('importedFrom');
  const importedFrom =
    importedFromRaw != null && typeof importedFromRaw === 'object'
      ? ({ ...(importedFromRaw as ImportProvenance) } as ImportProvenance)
      : null;

  return {
    id,
    title: str(node, 'title'),
    description: str(node, 'description'),
    sourceType: (str(node, 'sourceType', 'none') as SourceType) ?? 'none',
    sourceUrl: str(node, 'sourceUrl'),
    sourceText: str(node, 'sourceText'),
    servings: str(node, 'servings'),
    prepMinutes: nullableNum(node, 'prepMinutes'),
    cookMinutes: nullableNum(node, 'cookMinutes'),
    nutritionBasis: readNutritionBasis(node.get('nutritionBasis')),
    notes: str(node, 'notes'),
    createdAt: num(node, 'createdAt', 0),
    createdBy: str(node, 'createdBy'),
    updatedAt: num(node, 'updatedAt', 0),
    updatedBy: str(node, 'updatedBy'),
    deletedAt: nullableNum(node, 'deletedAt'),
    importedFrom,
    collections,
    tags,
    ingredients,
    instructions,
    photos,
    cookDates,
    nutritionFacts,
  };
}

function readNutritionBasis(value: unknown): NutritionBasis {
  if (value === 'perServing' || value === 'perRecipe' || value === 'unknown') return value;
  return 'unknown';
}
